/**
 * A utility class for generating and managing payment request upload templates.
 * This class provides methods to generate sample CSV content, filenames,
 * and descriptions for payment request uploads based on specific options such as
 * including VAT, custom account numbers, or multi premiums.
 */
export class PaymentRequestUploadUtils
{
    private static readonly SAMPLE_DATA: (string | number | null)[][] = [
        [
            'REF001',
            'John Doe',
            250,
            'Tap fitting plus 1 hour labour',
            'john@doe.com',
            '0821231234',
            123456,
            60,
            12,
            'Business Display Name (Optional)',
        ],
        [
            'REF002',
            'Jane Doe',
            2556.56,
            'Install new bath (all inclusive)',
            'jane@doe.com',
            '0823214321',
            987654,
            60,
            3,
            null,
        ],
    ];

    private static readonly ACCOUNT_INDEX = 6;
    private static readonly MULTI_PREMIUM_INDEX = 8;

    /**
     * Returns a description of the expected upload file format.
     */
    public static getUploadFileFormatDescription(
        useVatInclusiveAmount: boolean,
        useCustomAccountNumbers: boolean,
        isMultiPremiumEnabled: boolean,
    ): string
    {
        const base = [
            ' reference number',
            ' client name',
            useVatInclusiveAmount ? ' amount(incl VAT)' : ' amount(excl VAT)',
            ' description',
            ' client email',
            ' client contact number'
        ];

        if (useCustomAccountNumbers)
        {
            base.push(' account number');
        }

        base.push(' expiry period');

        if (isMultiPremiumEnabled)
        {
            base.push(' multi premium option');
        }

        base.push(' business display name (optional)');

        return base.join(',');
    }

    /**
     * Generates a sample filename based on the selected options.
     */
    public static getSampleFileName(useCustomAccountNumbers: boolean, isMultiPremiumEnabled: boolean): string
    {
        const accountPart = useCustomAccountNumbers ? 'Account' : '';
        const multiPremiumPart = isMultiPremiumEnabled ? '_MultiPremium' : '';

        return `BulkUpload${accountPart}Template${multiPremiumPart}.csv`;
    }


    /**
     * Generates a CSV Blob containing sample file content based on the specified parameters.
     */
    public static generateSampleFileContentCsvBlob(
        useCustomAccountNumbers: boolean,
        isMultiPremiumEnabled: boolean): Blob
    {
        const content = this.generateSampleFileCsvContent(useCustomAccountNumbers, isMultiPremiumEnabled);
        return this.createCsvBlob(content);
    }

    /**
     * Generates CSV content from the sample data with optional columns.
     */
    protected static generateSampleFileCsvContent(
        useCustomAccountNumbers: boolean,
        isMultiPremiumEnabled: boolean): string
    {
        return this.SAMPLE_DATA.map(row =>
        {
            const newRow = [...row];

            // Remove optional columns if disabled
            if (!useCustomAccountNumbers)
            {
                newRow.splice(this.ACCOUNT_INDEX, 1);
            }

            if (!isMultiPremiumEnabled)
            {
                const adjustedIndex = useCustomAccountNumbers ? this.MULTI_PREMIUM_INDEX : this.MULTI_PREMIUM_INDEX - 1;
                newRow.splice(adjustedIndex, 1);
            }

            return newRow.map(this.escapeCsvField).join(',');
        }).join('\n');
    }

    /**
     * Creates a Blob object from CSV content.
     */
    protected static createCsvBlob(content: string): Blob
    {
        return new Blob([content], {type: 'text/csv;charset=utf-8;'});
    }

    /**
     * Escapes a CSV field without wrapping in quotes.
     * - Null/undefined → empty string
     * - Removes quotes if present
     */
    private static escapeCsvField(field: unknown): string
    {
        if (field === null || field === undefined)
        {
            return '';
        }
        if (typeof field === 'string')
        {
            return field.replace(/"/g, '');
        }
        return String(field);
    }
}